<template>
  <aside class="filter-sidebar tw-rounded-card tw-border tw-border-slate-200 tw-bg-white tw-shadow-card">
    <div class="filter-sidebar__header tw-p-5 tw-pb-0">
      <div class="tw-mb-5 tw-flex tw-items-center tw-justify-between">
        <h2 class="tw-m-0 tw-text-lg tw-font-bold tw-text-slate-950">{{ $l('filters') }}</h2>
        <button type="button" class="tw-text-sm tw-font-medium tw-text-brand hover:tw-text-brand-dark tw-pressable" @click="clearFilters">
          {{ $l('clearAll') }}
        </button>
      </div>
    </div>

    <div class="filter-sidebar__body tw-space-y-6 tw-px-5">
      <section>
        <label class="tw-flex tw-cursor-pointer tw-items-start tw-gap-3 tw-rounded-xl tw-border tw-border-amber-200 tw-bg-amber-50/60 tw-py-3 tw-px-3 tw-text-sm tw-text-slate-700 hover:tw-bg-amber-50 tw-min-h-[44px]">
          <input
            type="checkbox"
            v-model="localFilters.hideAccessories"
            class="tw-mt-0.5"
            @change="onHideAccessoriesChange"
          >
          <span>
            <span class="tw-block tw-font-semibold tw-text-slate-900">{{ $l('hideAccessories') }}</span>
            <span class="tw-mt-0.5 tw-block tw-text-xs tw-text-slate-500">{{ $l('hideAccessoriesHint') }}</span>
          </span>
        </label>
      </section>

      <section>
        <h3 v-if="!hidePriceHistogram" class="tw-mb-3 tw-text-sm tw-font-semibold tw-text-slate-900">
          {{ $l('priceRange') }}
        </h3>
        <price-histogram-filter
          :products="products"
          :price-bounds="priceBounds"
          :min-price="localFilters.minPrice"
          :max-price="localFilters.maxPrice"
          :currency-sign="currencySign"
          :show-histogram="!hidePriceHistogram"
          :hide-accessories="localFilters.hideAccessories === true"
          :search-query="searchQuery"
          @update:minPrice="localFilters.minPrice = $event"
          @update:maxPrice="localFilters.maxPrice = $event"
          @bar-click="$emit('scroll-to-price-bracket', $event)"
        />
      </section>

      <section>
        <h3 class="tw-mb-3 tw-text-sm tw-font-semibold tw-text-slate-900">{{ $l('condition') }}</h3>
        <label
          v-for="condition in conditions"
          :key="condition.value"
          class="tw-mb-2 tw-flex tw-cursor-pointer tw-items-center tw-gap-3 tw-rounded-xl tw-py-3 tw-px-2 tw-text-sm tw-text-slate-700 hover:tw-bg-slate-50 tw-min-h-[44px]"
        >
          <input type="checkbox" :value="condition.value" v-model="localFilters.conditions">
          <span>{{ condition.label }}</span>
        </label>
      </section>

      <section>
        <h3 class="tw-mb-3 tw-text-sm tw-font-semibold tw-text-slate-900">{{ $l('stores') }}</h3>
        <label
          v-for="platform in platforms"
          :key="platform"
          class="tw-mb-2 tw-flex tw-cursor-pointer tw-items-center tw-gap-3 tw-rounded-xl tw-py-3 tw-px-2 tw-text-sm tw-text-slate-700 hover:tw-bg-slate-50 tw-min-h-[44px]"
        >
          <input type="checkbox" :value="platform" v-model="localFilters.platforms">
          <img class="tw-h-6 tw-w-6 tw-rounded-md tw-object-contain" :src="'/img/' + platform + '.png'" alt="">
          <span>{{ platform }}</span>
        </label>
      </section>

      <section>
        <h3 class="tw-mb-1 tw-text-sm tw-font-semibold tw-text-slate-900">{{ $l('storeCountry') }}</h3>
        <p class="tw-mb-3 tw-text-xs tw-text-slate-500">{{ $l('storeCountryHint') }}</p>
        <div class="tw-flex tw-flex-wrap tw-gap-2">
          <label
            v-for="country in countries"
            :key="country"
            class="tw-inline-flex tw-cursor-pointer tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-border-slate-200 tw-px-4 tw-py-2 tw-text-xs tw-font-medium tw-text-slate-700 hover:tw-border-brand tw-min-h-[44px]"
            :class="{ 'tw-border-brand tw-bg-brand-soft tw-text-brand-dark': localFilters.countries.includes(country) }"
          >
            <input class="tw-hidden" type="checkbox" :value="country" v-model="localFilters.countries">
            <img class="tw-h-3.5 tw-w-5 tw-rounded-[2px] tw-object-cover" :src="'/img/flags/' + country + '.png'" alt="">
            {{ country }}
          </label>
        </div>
      </section>
    </div>

    <div class="filter-sidebar__footer tw-border-t tw-border-slate-100 tw-bg-white tw-p-5 tw-pt-4">
      <button type="button" class="tw-primary-button tw-pressable tw-w-full tw-min-h-[48px]" @click="applyFilters">
        {{ $l('apply') }}
      </button>
    </div>
  </aside>
</template>

<script>
import {
  ALL_CONDITIONS,
  ALL_COUNTRIES,
  ALL_PLATFORMS,
  DEFAULT_FILTERS,
} from '@/utils/productFilters';
import PriceHistogramFilter from './PriceHistogramFilter.vue';

export default {
  name: 'FilterSidebar',
  components: {
    PriceHistogramFilter,
  },
  props: {
    value: {
      type: Object,
      default: () => ({ ...DEFAULT_FILTERS }),
    },
    priceBounds: {
      type: Object,
      default: () => ({ min: 0, max: 1000 }),
    },
    products: {
      type: Array,
      default: () => [],
    },
    currencySign: {
      type: String,
      default: '$',
    },
    hidePriceHistogram: {
      type: Boolean,
      default: false,
    },
    searchQuery: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      countries: ALL_COUNTRIES,
      platforms: ALL_PLATFORMS,
      localFilters: { ...DEFAULT_FILTERS },
    };
  },
  computed: {
    conditions() {
      return ALL_CONDITIONS.map((value) => ({
        value,
        label:
          value === 'NEW'
            ? this.$l('new')
            : value === 'USED'
              ? this.$l('used')
              : this.$l('refurbished'),
      }));
    },
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(next) {
        this.localFilters = {
          countries: [...(next.countries || [])],
          platforms: [...(next.platforms || [])],
          conditions: [...(next.conditions || [])],
          minPrice: next.minPrice != null ? next.minPrice : null,
          maxPrice: next.maxPrice != null ? next.maxPrice : null,
          hideAccessories: next.hideAccessories === true,
        };
      },
    },
  },
  methods: {
    applyFilters() {
      this.$emit('input', this.buildFilterPayload());
    },
    onHideAccessoriesChange() {
      this.$emit('input', this.buildFilterPayload());
    },
    buildFilterPayload() {
      return {
        countries: [...this.localFilters.countries],
        platforms: [...this.localFilters.platforms],
        conditions: [...this.localFilters.conditions],
        minPrice: this.localFilters.minPrice || null,
        maxPrice: this.localFilters.maxPrice || null,
        hideAccessories: this.localFilters.hideAccessories === true,
      };
    },
    clearFilters() {
      this.$emit('input', { ...DEFAULT_FILTERS });
    },
  },
};
</script>

<style scoped>
@media (min-width: 1024px) {
  .filter-sidebar {
    display: flex;
    flex-direction: column;
    max-height: var(--filter-sidebar-max-height, calc(100vh - 2rem));
    overflow: hidden;
  }

  .filter-sidebar__body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding-bottom: 1rem;
    scrollbar-gutter: stable;
  }

  .filter-sidebar__footer {
    flex-shrink: 0;
    border-bottom-left-radius: inherit;
    border-bottom-right-radius: inherit;
  }
}

.filter-sidebar__body::-webkit-scrollbar {
  width: 6px;
}

.filter-sidebar__body::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.55);
}
</style>
